import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import type { SupportedLanguage } from '@/i18n'

interface CatalogMetadataProps {
  language: SupportedLanguage
  title: string
  description: string
  canonicalPath: string
  alternatePaths: Record<SupportedLanguage, string>
}

export function CatalogMetadata({ language, title, description, canonicalPath, alternatePaths }: CatalogMetadataProps) {
  const { t } = useTranslation('common')
  useEffect(() => {
    const previousTitle = document.title
    document.title = t('catalog.metaTitle', { title })
    const restore: (() => void)[] = []
    function set(tag: 'meta' | 'link', selector: string, attributes: Record<string, string>) {
      const existing = document.head.querySelector(selector)
      const node = existing ?? document.createElement(tag)
      const old = node.getAttributeNames().map(name => [name, node.getAttribute(name)!] as const)
      Object.entries(attributes).forEach(([key, value]) => node.setAttribute(key, value))
      if (!existing) document.head.append(node)
      restore.push(() => {
        if (!existing) node.remove()
        else {
          node.getAttributeNames().forEach(name => node.removeAttribute(name))
          old.forEach(([name, value]) => node.setAttribute(name, value))
        }
      })
    }
    const origin = 'https://website2026-esamed-it.pages.dev'
    const canonical = `${origin}${canonicalPath}`
    set('meta', 'meta[name="description"]', { name: 'description', content: description })
    set('meta', 'meta[name="robots"]', { name: 'robots', content: 'index, follow' })
    set('link', 'link[rel="canonical"]', { rel: 'canonical', href: canonical })
    for (const locale of ['it', 'en'] as const) set('link', `link[rel="alternate"][hreflang="${locale}"]`, { rel: 'alternate', hreflang: locale, href: `${origin}${alternatePaths[locale]}` })
    set('link', 'link[rel="alternate"][hreflang="x-default"]', { rel: 'alternate', hreflang: 'x-default', href: `${origin}${alternatePaths.it}` })
    const pageTitle = t('catalog.metaTitle', { title })
    const socialImage = `${origin}/og-image.png`
    for (const [property, content] of Object.entries({
      'og:title': pageTitle,
      'og:description': description,
      'og:url': canonical,
      'og:type': 'website',
      'og:locale': language === 'it' ? 'it_IT' : 'en_GB',
      'og:site_name': t('brand.name'),
      'og:image': socialImage,
      'og:image:alt': t('brand.socialImageAlt'),
      'og:image:type': 'image/png',
      'og:image:width': '1200',
      'og:image:height': '630',
    })) set('meta', `meta[property="${property}"]`, { property, content })
    for (const [name, content] of Object.entries({ 'twitter:card': 'summary_large_image', 'twitter:title': pageTitle, 'twitter:description': description, 'twitter:image': socialImage, 'twitter:image:alt': t('brand.socialImageAlt') })) set('meta', `meta[name="${name}"]`, { name, content })
    return () => {
      document.title = previousTitle
      restore.forEach(action => action())
    }
  }, [alternatePaths, canonicalPath, description, language, t, title])
  return null
}
